/**
 * The application shell: sidebar, header, and the slot every page renders into. One layout at three
 * sizes — desktop sidebar (collapsible to an icon rail), a drawer below `lg`, and kiosk, where
 * `?kiosk=1` stands the chrome down entirely (see `useKioskMode`). The providers stay mounted
 * across all three, so nothing remounts when the toggle is hit.
 */

import { useCallback, useState } from 'react';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Outlet } from 'react-router-dom';

import { AppHeader } from '@/components/layout/AppHeader';
import { Brand } from '@/components/layout/Brand';
import { NavMenu } from '@/components/layout/NavMenu';
import { VersionPrompt } from '@/components/layout/VersionPrompt';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useKioskMode } from '@/hooks/useKioskMode';
import { ReportWorkspaceProvider } from '@/lib/reportWorkspace';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';

const SIDEBAR_COLLAPSED_KEY = 'winsight.sidebar-collapsed';

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function AppShell() {
  const { text } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { isKiosk } = useKioskMode();

  const setSidebarCollapsed = useCallback((next: boolean) => {
    setCollapsed(next);
    try {
      window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
    } catch {
      /* private mode / quota — preference is best-effort */
    }
  }, []);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(!collapsed);
  }, [collapsed, setSidebarCollapsed]);

  if (isKiosk) {
    return (
      <ReportWorkspaceProvider>
        <TooltipProvider delayDuration={300}>
          {/* No skip link or nav: nothing to skip to. `<main>` stays, so AT keeps a landmark (§4.9). */}
          <main id="main-content" className="min-h-dvh bg-background">
            <Outlet />
          </main>
        </TooltipProvider>
      </ReportWorkspaceProvider>
    );
  }

  return (
    <ReportWorkspaceProvider>
      <TooltipProvider delayDuration={300}>
        <div className="flex min-h-dvh bg-background">
          {/* First in the tab order: keyboard users must not tab the whole menu to reach the report (§4.9). */}
          <a
            href="#main-content"
            className="sr-only rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-50"
          >
            {text('Skip to the report')}
          </a>

          <aside
            className={cn(
              'sticky top-0 hidden h-dvh shrink-0 flex-col border-r border-shell-border bg-shell text-shell-foreground transition-[width] duration-200 ease-out lg:flex',
              collapsed ? 'w-12' : 'w-56',
            )}
          >
            {/* Same height as <AppHeader> so the brand band and top bar share one baseline. */}
            <div
              className={cn(
                'flex h-12 shrink-0 items-center border-b border-shell-border',
                collapsed ? 'justify-center px-1' : 'gap-1 px-2',
              )}
            >
              {collapsed ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={text('Expand the menu')}
                      aria-expanded={false}
                      onClick={toggleSidebar}
                      className="size-8 text-shell-foreground hover:bg-shell-hover hover:text-shell-foreground"
                    >
                      <FontAwesomeIcon icon={faChevronRight} className="size-3" aria-hidden />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">{text('Expand menu')}</TooltipContent>
                </Tooltip>
              ) : (
                <>
                  <Brand onShell className="min-w-0 flex-1" />
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={text('Collapse the menu')}
                        aria-expanded={true}
                        onClick={toggleSidebar}
                        className="size-7 shrink-0 text-shell-muted hover:bg-shell-hover hover:text-shell-foreground"
                      >
                        <FontAwesomeIcon icon={faChevronLeft} className="size-3" aria-hidden />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">{text('Collapse menu')}</TooltipContent>
                  </Tooltip>
                </>
              )}
            </div>
            <ScrollArea className="min-h-0 flex-1">
              <NavMenu
                collapsed={collapsed}
                onExpand={() => {
                  setSidebarCollapsed(false);
                }}
              />
            </ScrollArea>
            {!collapsed ? (
              <p className="shrink-0 border-t border-shell-border px-3 py-2 text-[10px] text-shell-muted">
                Build <span className="font-mono">{__BUILD_VERSION__}</span>
              </p>
            ) : null}
          </aside>

          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetContent
              side="left"
              className="w-72 gap-0 border-shell-border bg-shell p-0 text-shell-foreground sm:max-w-72"
            >
              <SheetHeader className="h-12 shrink-0 justify-center border-b border-shell-border px-3">
                <SheetTitle className="font-normal text-shell-foreground">
                  <Brand onShell />
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Reports grouped by operation, in the order work moves across the floor.
                </SheetDescription>
              </SheetHeader>
              <ScrollArea className="min-h-0 flex-1">
                <NavMenu
                  onNavigate={() => {
                    setMenuOpen(false);
                  }}
                />
              </ScrollArea>
            </SheetContent>
          </Sheet>

          <div className="flex min-w-0 flex-1 flex-col">
            <AppHeader
              onOpenMenu={() => {
                setMenuOpen(true);
              }}
            />
            <VersionPrompt />
            {/* The page gutter is deliberately tighter than the sidebar's padding — a report is a
                dense grid, and the frame around it does not earn width the figures could use. */}
            <main id="main-content" className="min-w-0 flex-1 px-2 py-1 sm:px-3">
              <Outlet />
            </main>
          </div>
        </div>
      </TooltipProvider>
    </ReportWorkspaceProvider>
  );
}
